import React, { useContext, useEffect, useState } from 'react';
import ContentPaper from '../../../../../components/layouts/rfs/Content';
import { Button, Grid, Space } from '@mantine/core';
import { ProjectOverview } from '../../projectOverview';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import AvicTable from '../table';
import { CustomTextArea } from '../../../../../common/inputs/CustomTextArea';
import { Provider, useAvic } from '../context';
import { AttachmentsProvider, useCrAttachments } from '../../attachments/context';
import { AttachmentsList } from '../../attachments/list';
import { calculateWeightedAverage, createPlantObject, getGeneralInfo, getPayload } from '../helper';
import { AppLoader } from '../../../../../components/common/loader';
import { CostingRequestsContext } from '../../../../../contexts/CostingRequest';
import { NotificationModal } from '../../../../../components/modal/NotificationModal';
import { notificationPopup } from '../../../../../constants';

const Content = () => {
  const { crNumber } = useParams();
  const { fetchAvicPlants, plantLoading, submit, saveLoading } = useAvic();
  const { info: crInfo, isCrRoleAuthorized } = useContext(CostingRequestsContext);
  const [plants, setPlants] = useState({});
  const [footerData, setFooterData] = useState([]);
  const [generalInfo, setGeneralInfo] = useState({
    skuId: null,
    stepId: null,
  });
  const {
    setAttachmentModal,
    fetchAttachments,
    attachmentModal,
    downloadAttachment,
    deleteAttachment,
    attachmentLoading,
    setDisableUploads,
  } = useCrAttachments();
  const [readOnly, setReadOnly] = useState(true);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);

  useEffect(() => {
    if (crInfo.producing_plant_data.length > 0) {
      fetch();
    }
  }, [crNumber, crInfo]);

  const setWAvg = (plantObject, countryDataArray) => {
    if (Array.isArray(countryDataArray) && countryDataArray.length > 0) {
      const updatedData = countryDataArray.map((countryData) => {
        if (countryData?.w_avg) {
          const wavg = calculateWeightedAverage(plantObject, countryData);
          if (parseFloat(wavg) - parseFloat(countryData.w_avg) !== 0) {
            return {
              ...countryData,
              custom_wavg: countryData.w_avg,
            };
          }
        }
        return countryData;
      });

      setFooterData(updatedData);
    } else {
      setFooterData(countryDataArray);
    }
  };

  const fetch = async () => {
    try {
      const data = await fetchAvicPlants('submission');
      const info = data?.[0] || {};

      const plantObject = createPlantObject(info.plant_level);
      const isAccessible = isCrRoleAuthorized(crInfo.producing_plant_data);
      let countryData = info?.country_level || [];
      setDisableUploads(false);
      setPlants(plantObject);
      setWAvg(Object.values(plantObject), countryData);
      setGeneralInfo(getGeneralInfo(info.plant_level));
      setReadOnly(info.status.toLowerCase() !== 'in progress' || !isAccessible);

      const stepId = info?.plant_level?.[0]?.step_id;
      if (stepId) {
        fetchStepAttachments(stepId);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchStepAttachments = (stepId) => {
    fetchAttachments(stepId);
  };

  const openUpladModal = () => {
    setAttachmentModal({
      ...attachmentModal,
      open: true,
      plantId: generalInfo.stepId,
      attachments: [],
    });
  };

  const onSubmit = async (action) => {
    setNotificationModalOpen(true);
    const payload = getPayload(Object.values(plants), footerData, generalInfo);
    await submit('submission', action, payload);
    fetch();
  };

  const Footer = () => (
    <Row className='justify-content-md-end' md={4}>
      <Col md='auto'>
        <Button
          className='next'
          variant='gradient'
          radius='md'
          size='md'
          color='dark'
          fullWidth
          style={{
            width: '200px',
          }}
          onClick={() => onSubmit('save')}
          disabled={readOnly}
        >
          {saveLoading ? <AppLoader size='sm' variant='dark' /> : 'Save'}
        </Button>
      </Col>
      <Col md='auto'>
        <Button
          className='next'
          variant='gradient'
          radius='md'
          size='md'
          fullWidth
          style={{
            marginRight: '1rem',
            width: '200px',
          }}
          onClick={() => onSubmit('submit')}
          disabled={readOnly}
        >
          {saveLoading ? <AppLoader size='sm' /> : 'Submit'}
        </Button>
      </Col>
    </Row>
  );

  const handleChange = (value, name) => {
    const updatedFooterData = footerData.map((item) => ({
      ...item,
      [name]: value,
    }));
    setFooterData(updatedFooterData);
  };

  return (
    <>
      <ContentPaper footer={<Footer />}>
        <ProjectOverview />
        <Space h='sm' />

        <AttachmentsList
          loading={attachmentLoading || plantLoading}
          attachments={attachmentModal?.existingAttachments || []}
          onDownload={(attachment) => downloadAttachment(attachment.name)}
          onRemove={(attachment) => deleteAttachment(attachment, generalInfo.stepId)}
          readOnly={readOnly}
        />

        <AvicTable
          data={Object.values(plants)}
          footerData={footerData}
          loading={plantLoading}
          upload={openUpladModal}
          setValues={setPlants}
          setFooterValues={setFooterData}
          disableUpload={!generalInfo.stepId || readOnly}
        />

        <Grid style={{ marginTop: '22px' }}>
          <Grid.Col span={12}>
            <CustomTextArea
              label={'Comment (If any)'}
              maxLength={255}
              minRows={4}
              onChange={(value) => handleChange(value, 'comment')}
              value={footerData[0]?.comment || ''}
              disabled={readOnly}
            />
          </Grid.Col>
        </Grid>
      </ContentPaper>
      <NotificationModal
        notificationModalOpen={notificationModalOpen}
        setNotificationModalOpen={setNotificationModalOpen}
        notificationText={notificationPopup.text}
        title={notificationPopup.title}
      />
    </>
  );
};

const AvicSubmission = () => (
  <Provider>
    <AttachmentsProvider>
      <Content />
    </AttachmentsProvider>
  </Provider>
);

export default AvicSubmission;
